"use client";

/**
 * BottomSheet 컴포넌트
 * @description 아래에서 올라오는 시트. 앱 DS `BottomSheet` 의 웹 대응물 — `OptionSheet` 이
 *   담기는 그릇이다. 웹에서는 `<dialog>` 를 쓴다: 포커스 가두기, `Esc` 닫기, 배경 비활성화를
 *   브라우저가 해 준다. `heightRatio` 는 검색이 있는 시트에만 준다 — 높이를 내용에 맞추면
 *   글자를 칠 때마다 시트가 발밑에서 움직인다.
 * @example
 * <BottomSheet visible={open} onClose={close} label="Country">…</BottomSheet>
 */
import { memo, useEffect, useRef } from "react";

import { bottomSheetStyles } from "./BottomSheet.styles";
import type { BottomSheetProps } from "./BottomSheet.types";

const BottomSheet = memo<BottomSheetProps>(
  ({ visible, onClose, heightRatio, label, children }) => {
    const ref = useRef<HTMLDialogElement>(null);

    useEffect(() => {
      const dialog = ref.current;
      if (!dialog) return;
      if (visible && !dialog.open) dialog.showModal();
      if (!visible && dialog.open) dialog.close();
    }, [visible]);

    return (
      <dialog
        ref={ref}
        aria-label={label}
        onClose={onClose}
        // 배경(::backdrop 바깥)을 눌러 닫는다 — dialog 요소 자체가 배경 영역까지 차지한다.
        onClick={(event) => {
          if (event.target === ref.current) onClose();
        }}
        className={bottomSheetStyles.dialog}
        style={{ height: "100%" }}
      >
        <div
          className={bottomSheetStyles.panel}
          style={heightRatio ? { height: heightRatio } : undefined}
        >
          {/* 잡고 내리는 손잡이 — 장식이라 읽어주지 않는다. */}
          <div className={bottomSheetStyles.grabberRow}>
            <span aria-hidden className={bottomSheetStyles.grabber} />
          </div>
          <div className={bottomSheetStyles.body}>{children}</div>
        </div>
      </dialog>
    );
  },
);

BottomSheet.displayName = "BottomSheet";

export default BottomSheet;
